import { useEffect, useRef, useState } from 'react'

import {
  type LessonSummary,
  type SessionDelivery,
  type SessionSummary,
} from '@/platform/learning/contract'
import { type LearningService } from '@/platform/learning/service'
import { type MaterialReader } from '@/platform/materials/extraction'
import { type MaterialLearner } from '@/platform/materials/learning'
import { type ReadAloudLearner } from '@/platform/read-aloud/api'
import { browserRecorder, type Recorder } from '@/platform/read-aloud/recorder'
import { newUuid } from '@/shared/id/uuid'

import { type DemoRunReport, type LoadSampleMaterials } from '../materials/model'
import { SchoolMaterialJourney } from '../materials/SchoolMaterialJourney'
import { ReadAloudScreen } from '../read-aloud/ReadAloudScreen'
import { ChildHome } from './ChildHome'
import { SubjectPage } from './ChildPages'
import { useFocusOnMount } from './focus'
import { LearningSession, type SubmittedAnswer } from './LearningSession'
import { SessionResult } from './SessionResult'
import { type Subject } from './subjects'

/**
 * The first Phase 2 slice (Execution Principles §7): Child Home → Learning
 * Session → Choice Activity → Feedback → Result → Child Home, entirely through
 * the `LearningService` contract. Child Home also leads to the subject pages
 * of the Slice 1 product shell and to the School Material Learning Journey
 * (Slice 2), which never receives the learning service or the learner's ID.
 *
 * A session the child closes stays in memory, so "Học tiếp" resumes it; the
 * same `clientSessionId` makes `startSession` return the same session. It is
 * not persisted across reloads: saved, resumable attempts (FR-44) are backend
 * work outside this slice (decision D-07).
 */

export interface ChildLearningProps {
  readonly learning: LearningService
  readonly learnerId: string
  readonly nickname: string
  /** DEMO school materials, local/development only (D-02); null otherwise. */
  readonly demoMaterials?: LoadSampleMaterials | null
  /** Reads real school material for this learner (Slice 3); null where it may not run. */
  readonly materialReader?: MaterialReader | null
  /** School-material lessons (D-17), bound to the learner session. */
  readonly materialLearner?: MaterialLearner | null
  /** Read Aloud (KT1 integrated), bound to the learner session; null where it may not run. */
  readonly readAloud?: ReadAloudLearner | null
  /** The microphone recorder; injectable for tests. */
  readonly recorder?: Recorder
  /** Receives finished DEMO material runs for the in-memory parent preview. */
  readonly onDemoRun?: (report: DemoRunReport) => void
}

interface Progress {
  readonly lesson: LessonSummary
  readonly clientSessionId: string
  readonly delivery: SessionDelivery
  readonly index: number
  readonly answers: ReadonlyMap<string, SubmittedAnswer>
}

type Stage =
  /** `returning`: reached by navigation, not the first load (focus target). */
  | { kind: 'home'; returning: boolean }
  | { kind: 'subject'; subject: Subject }
  /** `samples`: open straight on the DEMO sample list (from a subject page). */
  | { kind: 'materials'; samples: boolean }
  | { kind: 'read_aloud' }
  | { kind: 'session' }
  | { kind: 'finishing'; failed: boolean }
  | { kind: 'result'; summary: SessionSummary; delivery: SessionDelivery }

export function ChildLearning({
  learning,
  learnerId,
  nickname,
  demoMaterials = null,
  materialReader = null,
  materialLearner = null,
  readAloud = null,
  recorder = browserRecorder,
  onDemoRun = () => undefined,
}: ChildLearningProps): React.JSX.Element {
  const [stage, setStage] = useState<Stage>({ kind: 'home', returning: false })
  // Read Aloud is offered only when the server says this family may use it.
  const [readAloudOffered, setReadAloudOffered] = useState(false)
  useEffect(() => {
    if (readAloud === null) return
    let active = true
    readAloud.status().then(
      (s) => {
        if (active) setReadAloudOffered(s.learner_session && s.available)
      },
      () => undefined,
    )
    return () => {
      active = false
    }
  }, [readAloud])
  const [progress, setProgress] = useState<Progress | null>(null)
  const [starting, setStarting] = useState(false)
  const [startFailed, setStartFailed] = useState(false)
  const startInFlight = useRef(false)

  const start = async (lesson: LessonSummary) => {
    if (startInFlight.current) return
    startInFlight.current = true
    setStarting(true)
    setStartFailed(false)
    const resume = progress?.lesson.lesson_id === lesson.lesson_id ? progress : null
    const clientSessionId = resume?.clientSessionId ?? newUuid()
    try {
      const delivery = await learning.startSession(
        learnerId,
        lesson.lesson_id,
        clientSessionId,
      )
      setProgress(
        resume?.delivery.session_id === delivery.session_id
          ? { ...resume, delivery }
          : { lesson, clientSessionId, delivery, index: 0, answers: new Map() },
      )
      setStage({ kind: 'session' })
    } catch {
      setStartFailed(true)
    } finally {
      startInFlight.current = false
      setStarting(false)
    }
  }

  const finish = async (delivery: SessionDelivery) => {
    setStage({ kind: 'finishing', failed: false })
    try {
      const summary = await learning.completeSession(delivery.session_id)
      setStage({ kind: 'result', summary, delivery })
    } catch {
      setStage({ kind: 'finishing', failed: true })
    }
  }

  const goHome = () => {
    setStartFailed(false)
    setStage({ kind: 'home', returning: true })
  }

  switch (stage.kind) {
    case 'home':
      return (
        <ChildHome
          learning={learning}
          learnerId={learnerId}
          nickname={nickname}
          inProgressLessonId={progress?.lesson.lesson_id ?? null}
          starting={starting}
          startFailed={startFailed}
          onStart={(lesson) => void start(lesson)}
          onOpenSubject={(subject) => {
            setStartFailed(false)
            setStage({ kind: 'subject', subject })
          }}
          onOpenSchoolMaterials={() => {
            setStage({ kind: 'materials', samples: false })
          }}
          {...(readAloud !== null && readAloudOffered
            ? {
                onOpenReadAloud: () => {
                  setStage({ kind: 'read_aloud' })
                },
              }
            : {})}
          samplesAvailable={demoMaterials !== null}
          focusGreeting={stage.returning}
        />
      )

    case 'subject':
      return (
        <SubjectPage
          subject={stage.subject}
          learning={learning}
          learnerId={learnerId}
          inProgressLessonId={progress?.lesson.lesson_id ?? null}
          starting={starting}
          startFailed={startFailed}
          onStart={(lesson) => void start(lesson)}
          onBack={goHome}
          onTrySample={
            demoMaterials === null
              ? null
              : () => {
                  setStage({ kind: 'materials', samples: true })
                }
          }
        />
      )

    case 'materials':
      return (
        <SchoolMaterialJourney
          loadSamples={demoMaterials}
          reader={materialReader}
          learner={materialLearner}
          startWithSamples={stage.samples}
          onDemoRun={onDemoRun}
          onHome={goHome}
        />
      )

    case 'read_aloud':
      if (readAloud === null) return <></>
      return <ReadAloudScreen learner={readAloud} recorder={recorder} onHome={goHome} />

    case 'session':
      if (progress === null) return <></>
      return (
        <LearningSession
          learning={learning}
          lessonTitle={progress.lesson.title}
          delivery={progress.delivery}
          index={progress.index}
          answers={progress.answers}
          onSubmitted={(itemId, answer) => {
            setProgress((p) =>
              p === null ? p : { ...p, answers: new Map(p.answers).set(itemId, answer) },
            )
          }}
          onNext={() => {
            if (progress.index < progress.delivery.items.length - 1) {
              setProgress({ ...progress, index: progress.index + 1 })
            } else {
              void finish(progress.delivery)
            }
          }}
          onClose={goHome}
        />
      )

    case 'finishing':
      return (
        <Finishing
          key={String(stage.failed)}
          failed={stage.failed}
          onRetry={() => {
            if (progress !== null) void finish(progress.delivery)
          }}
          onHome={goHome}
        />
      )

    case 'result':
      return (
        <SessionResult
          summary={stage.summary}
          delivery={stage.delivery}
          // Supplementary practice from the backend's suggestions: a new
          // session of that practice lesson (first-learning-journey §4).
          onStartPractice={(lesson) => void start(lesson)}
          starting={starting}
          startFailed={startFailed}
          onHome={() => {
            // The session is finished; Child Home asks the service again.
            setProgress(null)
            goHome()
          }}
        />
      )
  }
}

/** Waiting for, or failing to get, the session summary. Focus follows the state. */
function Finishing({
  failed,
  onRetry,
  onHome,
}: {
  failed: boolean
  onRetry: () => void
  onHome: () => void
}): React.JSX.Element {
  const status = useFocusOnMount<HTMLParagraphElement>()
  const retry = useFocusOnMount<HTMLButtonElement>()
  return (
    <div className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center gap-4 px-5 pb-16 text-center">
      {failed ? (
        <>
          <p role="alert" className="text-lg">
            Chưa tổng kết được bài học. Con thử lại nhé.
          </p>
          <button
            ref={retry}
            type="button"
            className="h-12 rounded-xl bg-sky-700 px-6 text-lg font-semibold text-white"
            onClick={onRetry}
          >
            Thử lại
          </button>
          <button
            type="button"
            className="px-4 text-lg text-slate-700 underline dark:text-slate-300"
            onClick={onHome}
          >
            Về trang chính
          </button>
        </>
      ) : (
        <p
          ref={status}
          tabIndex={-1}
          role="status"
          className="text-lg text-slate-700 dark:text-slate-300"
        >
          Đang tổng kết bài học…
        </p>
      )}
    </div>
  )
}
